import { BolsoLayout } from "@/components/BolsoLayout";
import { TransactionForm } from "@/components/TransactionForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Download, FileText } from "lucide-react";
import { useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Legend,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { useI18n } from "@/contexts/I18nContext";
import * as XLSX from "xlsx";
import { toast } from "sonner";

const CHART_COLORS = [
  "#3d8b3d","#2196F3","#FF9800","#E91E63","#9C27B0",
  "#00BCD4","#FF5722","#607D8B","#795548","#4CAF50",
];

export default function Reports() {
  const { formatCurrency, formatDate, categoryName, t, ui } = useI18n();
  const now = new Date();
  const [showForm, setShowForm] = useState(false);
  const [period, setPeriod] = useState<"monthly" | "annual">("monthly");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [categoryFilter, setCategoryFilter] = useState<string>("all");

  // Stable date range for transaction list query
  const dateRange = useMemo(() => {
    if (period === "monthly") {
      return {
        dateFrom: new Date(year, month - 1, 1),
        dateTo: new Date(year, month, 0, 23, 59, 59),
      };
    } else {
      return {
        dateFrom: new Date(year, 0, 1),
        dateTo: new Date(year, 11, 31, 23, 59, 59),
      };
    }
  }, [period, month, year]);

  // Server-side aggregates for summary cards (fast, accurate)
  const { data: summary, isLoading: summaryLoading } = trpc.transactions.monthSummary.useQuery(
    { month, year },
    { enabled: period === "monthly" }
  );

  // Transaction list for the detail table (bounded limit)
  const { data: transactions = [], isLoading: txLoading } = trpc.transactions.list.useQuery({
    limit: 200,
    dateFrom: dateRange.dateFrom,
    dateTo: dateRange.dateTo,
  });

  const { data: categories = [] } = trpc.categories.list.useQuery({});
  const { data: monthlyTotals = [] } = trpc.transactions.monthlyTotals.useQuery({ months: 12 });
  const { data: categorySpending = [], isLoading: catLoading } = trpc.transactions.categorySpending.useQuery(
    { month, year },
    { enabled: period === "monthly" }
  );

  // For annual period, compute totals from transaction list (no server aggregate for annual)
  const annualIncome = useMemo(
    () => transactions.filter((t) => t.type === "income").reduce((s, t) => s + parseFloat(String(t.amount)), 0),
    [transactions]
  );
  const annualExpense = useMemo(
    () => transactions.filter((t) => t.type === "expense").reduce((s, t) => s + parseFloat(String(t.amount)), 0),
    [transactions]
  );
  // Category breakdown — use server aggregate for monthly, compute from list for annual
  const catBreakdown = useMemo(() => {
    if (period === "monthly") {
      return categorySpending
        .map((cs) => {
          const cat = categories.find((c) => c.id === cs.categoryId);
          return { name: categoryName(cat?.name) || categoryName("Outros"), value: cs.total };
        })
        .filter((c) => c.value > 0)
        .sort((a, b) => b.value - a.value);
    } else {
      return categories.map((cat) => {
        const total = transactions
          .filter((t) => t.categoryId === cat.id && t.type === "expense")
          .reduce((s, t) => s + parseFloat(String(t.amount)), 0);
        return { name: categoryName(cat.name), value: total };
      }).filter((c) => c.value > 0).sort((a, b) => b.value - a.value);
    }
  }, [period, categorySpending, categories, transactions]);

  // Filter transaction list for the detail table
  const filtered = useMemo(() => {
    return transactions.filter((tx) => {
      return categoryFilter === "all" || String(tx.categoryId) === categoryFilter;
    });
  }, [transactions, categoryFilter]);

  const filteredIncome = useMemo(
    () => filtered.reduce((s, tx) => tx.type === "income" ? s + parseFloat(String(tx.amount)) : s, 0),
    [filtered]
  );
  const filteredExpense = useMemo(
    () => filtered.reduce((s, tx) => tx.type === "expense" ? s + parseFloat(String(tx.amount)) : s, 0),
    [filtered]
  );
  const transferTotal = useMemo(
    () => filtered.reduce((s, tx) => tx.type === "transfer" ? s + parseFloat(String(tx.amount)) : s, 0),
    [filtered]
  );

  // Com filtro de categoria, os cartões devem acompanhar a tabela filtrada.
  // Sem filtro, o resumo mensal do servidor continua sendo a fonte mais precisa.
  const hasCategoryFilter = categoryFilter !== "all";
  const income = hasCategoryFilter ? filteredIncome : period === "monthly" ? (summary?.income ?? 0) : annualIncome;
  const expense = hasCategoryFilter ? filteredExpense : period === "monthly" ? (summary?.expense ?? 0) : annualExpense;
  const balance = income - expense;

  const isLoading = summaryLoading || txLoading;

  function exportToExcel() {
    const periodLabel = period === "monthly"
      ? `${t.common.months[month - 1].slice(0, 3)}_${year}`
      : `${year}`;

    // Sheet 1: Transactions
    const txRows = filtered.map((tx) => {
      const cat = categories.find((c) => c.id === tx.categoryId);
      return {
        [ui.reports.excel.date]: formatDate(tx.date, { day: "2-digit", month: "2-digit", year: "numeric" }),
        [ui.reports.excel.description]: tx.description || "",
        [ui.reports.excel.category]: categoryName(cat?.name),
        [ui.reports.excel.type]: tx.type === "income" ? t.transactions.income : tx.type === "expense" ? t.transactions.expense : t.transactions.transfer,
        [ui.reports.excel.amount]: parseFloat(String(tx.amount)),
      };
    });

    // Sheet 2: Summary
    const summaryRows = [
      { [ui.reports.excel.item]: t.reports.income, [ui.reports.excel.amount]: income },
      { [ui.reports.excel.item]: t.reports.expenses, [ui.reports.excel.amount]: expense },
      { [ui.reports.excel.item]: t.reports.balance, [ui.reports.excel.amount]: balance },
    ];

    // Sheet 3: Category breakdown
    const catRows = catBreakdown.map((c) => ({
      [ui.reports.excel.category]: c.name,
      [ui.reports.excel.total]: c.value,
      [ui.reports.excel.percentage]: catBreakdown.reduce((a, x) => a + x.value, 0) > 0
        ? `${((c.value / catBreakdown.reduce((a, x) => a + x.value, 0)) * 100).toFixed(1)}%`
        : "0%",
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(txRows), ui.reports.excel.transactionsSheet);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), ui.reports.excel.summarySheet);
    if (catRows.length > 0) {
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(catRows), ui.reports.excel.categoriesSheet);
    }

    XLSX.writeFile(wb, `bolso_relatorio_${periodLabel}.xlsx`);
    toast.success(ui.reports.exportExcel);
  }

  return (
    <BolsoLayout onNewTransaction={() => setShowForm(true)}>
      <div className="p-4 lg:p-6 max-w-5xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-serif font-bold text-foreground">{t.reports.title}</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={exportToExcel}
              disabled={filtered.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-border bg-background hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={ui.reports.exportExcel}
            >
              <Download className="w-3.5 h-3.5" />
              Excel
            </button>
            <Select value={period} onValueChange={(v) => setPeriod(v as "monthly" | "annual")}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">{t.reports.monthly}</SelectItem>
                <SelectItem value="annual">{t.reports.annual}</SelectItem>
              </SelectContent>
            </Select>
            {period === "monthly" && (
              <Select value={`${month}-${year}`} onValueChange={(v) => {
                const [m, y] = v.split("-").map(Number);
                setMonth(m); setYear(y);
              }}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => {
                    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
                    return (
                      <SelectItem key={i} value={`${d.getMonth() + 1}-${d.getFullYear()}`}>
                        {t.common.months[d.getMonth()].slice(0, 3)} {d.getFullYear()}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            )}
            {period === "annual" && (
              <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[now.getFullYear() - 1, now.getFullYear()].map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder={t.transactions.category} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{ui.reports.allCategories}</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>{categoryName(c.name)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="border-border shadow-sm">
                <CardContent className="p-3 sm:p-4">
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-7 w-28" />
                </CardContent>
              </Card>
            ))
          ) : (
            [
              { label: t.reports.income, value: income, color: "income-text" },
              { label: t.reports.expenses, value: expense, color: "expense-text" },
              { label: t.reports.balance, value: balance, color: balance >= 0 ? "income-text" : "expense-text" },
              { label: t.transactions.transfer, value: transferTotal, color: "text-blue-600 dark:text-blue-400" },
            ].map(({ label, value, color }) => (
              <Card key={label} className="border-border shadow-sm">
                <CardContent className="p-3 sm:p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{label}</p>
                  <p className={cn("text-base sm:text-xl font-bold mt-1 font-serif tabular-nums", color)}>
                    {formatCurrency(value)}
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {period === "annual" && (
            <Card className="border-border shadow-sm lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">{ui.reports.monthlyIncomeVsExpenses}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={monthlyTotals} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="income" name={t.reports.income} fill="var(--income)" radius={[3,3,0,0]} />
                    <Bar dataKey="expense" name={t.reports.expenses} fill="var(--expense)" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {catBreakdown.length > 0 && (
            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">{t.dashboard.expensesByCategory}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={catBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                      {catBreakdown.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {catBreakdown.length > 0 && (
            <Card className="border-border shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">{ui.reports.categoryRanking}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {catBreakdown.slice(0, 6).map((item, i) => {
                    const total = catBreakdown.reduce((a, c) => a + c.value, 0);
                    const pct = total > 0 ? (item.value / total) * 100 : 0;
                    return (
                      <div key={item.name} className="flex items-center gap-3 px-4 py-2.5">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="flex-1 text-sm font-medium text-foreground">{item.name}</span>
                        <div className="w-20 bg-muted rounded-full h-1.5 hidden sm:block">
                          <div className="h-1.5 rounded-full bg-primary" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-muted-foreground w-8 text-right">{pct.toFixed(0)}%</span>
                        <span className="text-sm font-semibold expense-text tabular-nums">{formatCurrency(item.value)}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Transaction list */}
        <Card className="border-border shadow-sm">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">
              {ui.reports.transactionsCount} ({filtered.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {txLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{ui.reports.noTransactionsPeriod}</p>
              </div>
            ) : (
              <div className="divide-y divide-border max-h-96 overflow-y-auto">
                {filtered.map((tx) => {
                  const cat = categories.find((c) => c.id === tx.categoryId);
                  return (
                    <div key={tx.id} className="flex items-center gap-3 px-4 py-3">
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0",
                        tx.type === "income" ? "income-badge" :
                        tx.type === "transfer" ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" :
                        "expense-badge"
                      )}>
                        {tx.type === "income" ? "↑" : tx.type === "transfer" ? "↔" : "↓"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {tx.description || categoryName(cat?.name) || ui.common.noDescription}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(tx.date, { day: "2-digit", month: "2-digit", year: "numeric" })}
                          {cat && <> · {categoryName(cat.name)}</>}
                        </p>
                      </div>
                      <span className={cn(
                        "text-sm font-semibold tabular-nums",
                        tx.type === "income" ? "income-text" :
                        tx.type === "transfer" ? "text-blue-600 dark:text-blue-400" :
                        "expense-text"
                      )}>
                        {tx.type === "income" ? "+" : tx.type === "transfer" ? "↔" : "-"}
                        {formatCurrency(parseFloat(String(tx.amount)))}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <TransactionForm open={showForm} onClose={() => setShowForm(false)} />
    </BolsoLayout>
  );
}
